import { HttpCoreService } from "../services/HttpCoreService";
import {
  ActivationChecklist,
  SiteReadinessData,
  DocumentReview,
  DocumentReviewResponse,
  ActivationWorkflowStatus,
  CreateChecklistTemplateRequest,
  ChecklistTemplate,
  DashboardResponse,
  DashboardFilters,
  DashboardSummary,
  AuditHistoryResponse,
  AuditHistoryFilters,
  CreateActivationRequest,
  AddChecklistItemRequest,
  SiteActivationTask,
  AssignableUser,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
  TasksResponse,
  StudyChecklistConfig,
  StudyChecklistConfigItem,
  WaivedGlobalItem,
  CreateStudyChecklistConfigRequest,
  AddStudyConfigItemRequest,
  WaiveGlobalItemRequest,
  ExcludeSiteItemRequest,
  AddSiteExceptionItemRequest,
  ChecklistItem,
} from "../types/siteActivation/siteActivation.type";

export interface ISiteActivationRepository {
  // Checklist template operations
  createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate>;

  // Checklist operations
  getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist>;
  updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist>;
  submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Activation creation and item management (simplified flow)
  createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist>;
  addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist>;
  removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }>;
  submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist>;

  // Site readiness operations
  getSiteReadiness(studyId: string, siteId: string): Promise<SiteReadinessData>;

  // Document operations
  getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]>;
  uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview>;
  approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview>;
  rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview>;
  deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }>;
  getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ url: string; fileName: string }>;

  // Workflow operations
  getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus>;

  // PM-specific workflow operations
  startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus>;
  rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus>;

  // Dashboard operations
  getDashboard(filters?: DashboardFilters): Promise<DashboardResponse>;
  getDashboardSummary(studyId?: string): Promise<DashboardSummary>;

  // Export operations
  exportToCSV(filters?: DashboardFilters): Promise<Blob>;

  // Audit history
  getAuditHistory(filters?: AuditHistoryFilters): Promise<AuditHistoryResponse>;

  // Options/metadata
  getCategoryOptions(): Promise<{ value: string; label: string }[]>;

  // Task operations
  getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse>;
  getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask>;
  createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask>;
  updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask>;
  deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }>;
  getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]>;

  // Study Checklist Configuration operations
  getStudyChecklistConfig(studyId: string): Promise<StudyChecklistConfig>;
  createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig>;
  addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem>;
  waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem>;

  // Site Item Exclusion and Exception operations
  excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem>;
  addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem>;
}

export class SiteActivationRepository implements ISiteActivationRepository {
  constructor(private httpService: HttpCoreService) {}

  async createChecklistTemplate(
    data: CreateChecklistTemplateRequest,
  ): Promise<ChecklistTemplate> {
    const response = await this.httpService.post<ChecklistTemplate>(
      `/activation/templates`,
      data,
    );
    return response.data;
  }

  async getChecklistTemplate(
    studyId: string,
    siteId: string,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/checklist`,
    );
    return response.data as ActivationChecklist;
  }

  async updateChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: Partial<ActivationChecklist["items"][0]>,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.patch(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/items/${itemId}`,
      data,
    );
    return response.data as ActivationChecklist;
  }

  async submitChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/submit`,
      { notes },
    );
    return response.data as ActivationChecklist;
  }

  async createActivation(
    studyId: string,
    siteId: string,
    data: CreateActivationRequest,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation`,
      data,
    );
    return response.data as ActivationChecklist;
  }

  async addChecklistItem(
    studyId: string,
    siteId: string,
    data: AddChecklistItemRequest,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/items`,
      data,
    );
    return response.data as ActivationChecklist;
  }

  async removeChecklistItem(
    studyId: string,
    siteId: string,
    itemId: string,
  ): Promise<{ message: string }> {
    const response = await this.httpService.delete(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/items/${itemId}`,
    );
    return response.data as { message: string };
  }

  async submitChecklistDraft(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationChecklist> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/submit-draft`,
      { notes },
    );
    return response.data as ActivationChecklist;
  }

  async getSiteReadiness(
    studyId: string,
    siteId: string,
  ): Promise<SiteReadinessData> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/readiness`,
    );
    return response.data as SiteReadinessData;
  }

  async getDocumentReviews(
    studyId: string,
    siteId: string,
  ): Promise<DocumentReview[]> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/documents`,
    );
    const data = response.data as DocumentReviewResponse;
    return data.documents || [];
  }

  async uploadDocument(
    studyId: string,
    siteId: string,
    documentData: FormData,
  ): Promise<DocumentReview> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/documents/upload`,
      documentData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data as DocumentReview;
  }

  async approveDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<DocumentReview> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/documents/${documentId}/approve`,
      {},
    );
    return response.data as DocumentReview;
  }

  async rejectDocument(
    studyId: string,
    siteId: string,
    documentId: string,
    reason: string,
  ): Promise<DocumentReview> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/documents/${documentId}/reject`,
      { reason },
    );
    return response.data as DocumentReview;
  }

  async deleteDocument(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ message: string }> {
    const response = await this.httpService.delete(
      `/studies/${studyId}/sites/${siteId}/activation/documents/${documentId}`,
    );
    return response.data as { message: string };
  }

  async getDocumentDownloadUrl(
    studyId: string,
    siteId: string,
    documentId: string,
  ): Promise<{ url: string; fileName: string }> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/documents/${documentId}/download-url`,
    );
    return response.data as { url: string; fileName: string };
  }

  async getActivationStatus(
    studyId: string,
    siteId: string,
  ): Promise<ActivationWorkflowStatus> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/status`,
    );
    return response.data as ActivationWorkflowStatus;
  }

  // PM-specific workflow operations
  async startInitiation(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/start-initiation`,
      { notes },
    );
    return response.data as ActivationWorkflowStatus;
  }

  async approveChecklist(
    studyId: string,
    siteId: string,
    notes?: string,
  ): Promise<ActivationWorkflowStatus> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/approve-checklist`,
      { notes },
    );
    return response.data as ActivationWorkflowStatus;
  }

  async rejectChecklist(
    studyId: string,
    siteId: string,
    reason: string,
  ): Promise<ActivationWorkflowStatus> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/reject-checklist`,
      { reason },
    );
    return response.data as ActivationWorkflowStatus;
  }

  // Dashboard operations
  async getDashboard(filters?: DashboardFilters): Promise<DashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.status) {
      params.append("status", filters.status);
    }
    if (filters?.priority) {
      params.append("priority", filters.priority);
    }

    const queryString = params.toString();
    const response = await this.httpService.get(
      `/activation/dashboard${queryString ? `?${queryString}` : ""}`,
    );
    const raw = response.data as Partial<DashboardResponse> & {
      activations?: DashboardResponse["items"];
    };

    const items = raw.items ?? raw.activations ?? [];

    return {
      ...raw,
      items,
      activations: raw.activations ?? items,
      total: raw.total ?? items.length,
      page: raw.page ?? 1,
      limit: raw.limit ?? items.length,
      summary: raw.summary as DashboardResponse["summary"],
    };
  }

  async getDashboardSummary(studyId?: string): Promise<DashboardSummary> {
    const queryString = studyId ? `?studyId=${studyId}` : "";
    const response = await this.httpService.get(
      `/activation/dashboard/summary${queryString}`,
    );
    return response.data as DashboardSummary;
  }

  // Export operations
  async exportToCSV(filters?: DashboardFilters): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.status) {
      params.append("status", filters.status);
    }
    params.append("format", "csv");

    const queryString = params.toString();
    const response = await this.httpService.get(
      `/activation/export?${queryString}`,
      { responseType: "blob" },
    );
    return response.data as Blob;
  }

  // Audit history
  async getAuditHistory(
    filters?: AuditHistoryFilters,
  ): Promise<AuditHistoryResponse> {
    const params = new URLSearchParams();
    if (filters?.studyId) {
      params.append("studyId", filters.studyId);
    }
    if (filters?.siteId) {
      params.append("siteId", filters.siteId);
    }
    if (filters?.action) {
      params.append("action", filters.action);
    }
    if (filters?.page) {
      params.append("page", filters.page.toString());
    }
    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }

    const queryString = params.toString();
    const response = await this.httpService.get(
      `/activation/history${queryString ? `?${queryString}` : ""}`,
    );
    return response.data as AuditHistoryResponse;
  }

  // Options/metadata
  async getCategoryOptions(): Promise<{ value: string; label: string }[]> {
    const response = await this.httpService.get<
      { value: string; label: string }[]
    >(`/activation/templates/options/categories`);
    return response.data;
  }

  // Task operations
  async getTasks(
    studyId: string,
    siteId: string,
    filters?: TaskFilters,
  ): Promise<TasksResponse> {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== "All") {
      params.append("status", filters.status);
    }
    if (filters?.priority && filters.priority !== "All") {
      params.append("priority", filters.priority);
    }
    if (filters?.assignedToId) {
      params.append("assignedToId", filters.assignedToId);
    }
    if (filters?.searchQuery) {
      params.append("search", filters.searchQuery);
    }
    if (filters?.page) {
      params.append("page", filters.page.toString());
    }
    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }

    const queryString = params.toString();
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/tasks${queryString ? `?${queryString}` : ""}`,
    );
    return response.data as TasksResponse;
  }

  async getTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<SiteActivationTask> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/tasks/${taskId}`,
    );
    return response.data as SiteActivationTask;
  }

  async createTask(
    studyId: string,
    siteId: string,
    data: CreateTaskRequest,
  ): Promise<SiteActivationTask> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/tasks`,
      data,
    );
    return response.data as SiteActivationTask;
  }

  async updateTask(
    studyId: string,
    siteId: string,
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<SiteActivationTask> {
    const response = await this.httpService.patch(
      `/studies/${studyId}/sites/${siteId}/activation/tasks/${taskId}`,
      data,
    );
    return response.data as SiteActivationTask;
  }

  async deleteTask(
    studyId: string,
    siteId: string,
    taskId: string,
  ): Promise<{ message: string }> {
    const response = await this.httpService.delete(
      `/studies/${studyId}/sites/${siteId}/activation/tasks/${taskId}`,
    );
    return response.data as { message: string };
  }

  async getAssignableUsers(
    studyId: string,
    siteId: string,
  ): Promise<AssignableUser[]> {
    const response = await this.httpService.get(
      `/studies/${studyId}/sites/${siteId}/activation/users/assignable`,
    );
    return response.data as AssignableUser[];
  }

  async getStudyChecklistConfig(
    studyId: string,
  ): Promise<StudyChecklistConfig> {
    const response = await this.httpService.get(
      `/studies/${studyId}/activation-config`,
    );
    return response.data as StudyChecklistConfig;
  }

  async createStudyChecklistConfig(
    studyId: string,
    data: CreateStudyChecklistConfigRequest,
  ): Promise<StudyChecklistConfig> {
    const response = await this.httpService.post(
      `/studies/${studyId}/activation-config`,
      data,
    );
    return response.data as StudyChecklistConfig;
  }

  async addStudyConfigItem(
    studyId: string,
    data: AddStudyConfigItemRequest,
  ): Promise<StudyChecklistConfigItem> {
    const response = await this.httpService.post(
      `/studies/${studyId}/activation-config/items`,
      data,
    );
    return response.data as StudyChecklistConfigItem;
  }

  async waiveGlobalItem(
    studyId: string,
    data: WaiveGlobalItemRequest,
  ): Promise<WaivedGlobalItem> {
    const response = await this.httpService.post(
      `/studies/${studyId}/activation-config/waivers`,
      data,
    );
    return response.data as WaivedGlobalItem;
  }

  async excludeSiteItem(
    studyId: string,
    siteId: string,
    itemId: string,
    data: ExcludeSiteItemRequest,
  ): Promise<ChecklistItem> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/items/${itemId}/exclude`,
      data,
    );
    return response.data as ChecklistItem;
  }

  async addSiteExceptionItem(
    studyId: string,
    siteId: string,
    data: AddSiteExceptionItemRequest,
  ): Promise<ChecklistItem> {
    const response = await this.httpService.post(
      `/studies/${studyId}/sites/${siteId}/activation/checklist/site-items`,
      data,
    );
    return response.data as ChecklistItem;
  }
}
